import { useCallback, useRef, useEffect, useMemo } from 'react'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import { rememberDownloadOrigin } from 'Utils/downloadTracking'
import ImpressionsBuffer from 'Utils/ImpressionsBuffer'
import { prepareTrackingItem } from 'Utils/PrepareTrackingItem'
import ResolveProductPricing from 'Utils/ResolveProductPricing'

function calculatePricing(productPricing, basePrice) {
  if (['saleOff', 'CLD'].includes(productPricing.ribbon) && productPricing.discount > 0) {
    return {
      price: productPricing.price.toFixed(2),
      discount: (productPricing.originalPrice - productPricing.price).toFixed(2),
    }
  }
  return { price: basePrice, discount: 0 }
}

// For click events (select_item, select_content) - receives itemAttributes from ModelCard
function prepareItemForClick(attributes, pageConfig) {
  if (!pageConfig?.cart || !attributes) {
    return null
  }

  const productPricing = ResolveProductPricing({
    cart: pageConfig.cart,
    attributes,
    id: attributes.id,
  })

  const { price, discount } = calculatePricing(productPricing, attributes.price)

  return prepareTrackingItem(attributes, { price, discount })
}

// For impression events (view_item_list) - receives model data from props
function prepareItemData(model, index, collectionSlug, pageConfig) {
  if (!pageConfig?.cart || !model) {
    return null
  }

  const {
    id: attrId,
    price: basePrice = 0,
    saleOffDiscount = 0,
    isSaleOffApplicable = false,
    isCLDApplicable = false,
    title,
    authorUsername,
    categoryTitle,
    category,
    subcategory,
    gaAttributes,
  } = model.attributes || model

  const {
    affiliation,
    itemCategory3,
    itemCategory4,
    itemCategory5,
    itemVariant,
    locationId,
  } = gaAttributes || {}

  const itemId = model.id || attrId

  const productPricing = ResolveProductPricing({
    cart: pageConfig.cart,
    attributes: {
      id: itemId,
      price: basePrice,
      saleOffDiscount,
      isSaleOffApplicable,
      isCLDApplicable,
    },
  })

  const { price, discount } = calculatePricing(
    productPricing,
    basePrice?.toFixed?.(2) || String(basePrice)
  )

  return prepareTrackingItem({
    id: itemId,
    title,
    affiliation,
    index,
    itemBrand: authorUsername,
    itemCategory: categoryTitle || category,
    subcategory,
    itemCategory3,
    itemCategory4,
    itemCategory5,
    itemListId: collectionSlug,
    itemListName: collectionSlug,
    itemVariant,
    locationId,
  }, { price, discount })
}

const useCollectionHubTracking = ({ models, collectionSlug, itemsPerRow }) => {
  const pageConfig = usePageConfig()
  const bufferRef = useRef(null)
  const ctrBufferRef = useRef([])
  const totalItemsRef = useRef(0)
  const trackedCountRef = useRef(0)

  if (!bufferRef.current) {
    bufferRef.current = new ImpressionsBuffer()
  }

  const modelsMap = useMemo(() => {
    // Reset tracking state when models change
    trackedCountRef.current = 0
    ctrBufferRef.current = []
    bufferRef.current?.clear?.()

    const map = new Map()
    models.forEach((model, idx) => {
      const numericId = Number(model.id)
      map.set(numericId, { model, index: idx })
    })
    return map
  }, [models])

  totalItemsRef.current = models.length

  const handleImpression = useCallback((itemId) => {
    if (!window.dataLayer || !pageConfig?.cart) {
      return
    }

    const modelData = modelsMap.get(itemId)
    if (!modelData) {
      return
    }

    const { model, index } = modelData
    const itemData = prepareItemData(model, index, collectionSlug, pageConfig)

    if (!itemData) {
      return
    }

    const wasAdded = bufferRef.current.addImpressionToQueue(itemData)

    if (!wasAdded) {
      return
    }

    ctrBufferRef.current.push(itemId)

    const shouldFire = bufferRef.current.getCount() >= itemsPerRow
      || (trackedCountRef.current + bufferRef.current.getCount()) >= totalItemsRef.current

    if (shouldFire) {
      const itemsToTrack = bufferRef.current.flushImpressions(itemsPerRow)

      if (itemsToTrack.length > 0 && typeof window.trackViewItemList === 'function') {
        window.trackViewItemList({
          itemListId: collectionSlug,
          itemListName: collectionSlug,
          items: itemsToTrack,
          userId: pageConfig?.currentUserId,
          eventSource: 'collection_hub',
        })
      }

      const ctrItemIds = ctrBufferRef.current.splice(0, itemsPerRow)
      if (typeof window.postCtrImpressions === 'function') {
        window.postCtrImpressions(ctrItemIds, { perPage: itemsPerRow })
      }

      trackedCountRef.current += itemsToTrack.length
    }
  }, [pageConfig, collectionSlug, itemsPerRow, modelsMap])

  // Click tracking (select_item, select_content)
  useEffect(() => {
    const enrichAttributes = (itemAttributes) => ({
      ...itemAttributes,
      itemListId: itemAttributes.itemListId || collectionSlug,
      itemListName: itemAttributes.itemListName || collectionSlug,
    })

    window.trackSelectItem = (itemAttributes) => {
      if (!window.dataLayer || !pageConfig?.cart) return

      const item = prepareItemForClick(enrichAttributes(itemAttributes), pageConfig)
      if (!item) return

      rememberDownloadOrigin({
        itemId: itemAttributes.id,
        pageType: 'collection_hub',
        itemListName: item.item_list_name || '',
      })

      window.dataLayer.push({
        event: 'select_item',
        user_id: pageConfig.currentUserId || '',
        event_source: 'collection_hub',
        ecommerce: {
          currency: 'USD',
          value: item.price,
          item_list_name: item.item_list_name,
          item_list_id: item.item_list_id,
          items: [item],
        },
        _clear: true,
      })
    }

    window.trackSelectContent = (itemAttributes) => {
      if (!window.dataLayer || !pageConfig?.cart) return

      const item = prepareItemForClick(enrichAttributes(itemAttributes), pageConfig)
      if (!item) return

      window.dataLayer.push({
        event: 'select_content',
        event_source: 'collection_hub',
        ecommerce: {
          item_list_name: item.item_list_name,
          item_list_id: item.item_list_id,
          items: [item],
        },
      })
    }

    return () => {
      delete window.trackSelectItem
      delete window.trackSelectContent
    }
  }, [pageConfig, collectionSlug])

  // Impression tracking (view_item_list)
  useEffect(() => {
    window.handleImpression = handleImpression

    const initItems = () => {
      models.forEach((model) => {
        const card = document.querySelector(`[data-model-id="${model.id}"]`)

        if (card) {
          const rect = card.getBoundingClientRect()
          const isInViewport = rect.top < window.innerHeight && rect.bottom > 0

          if (isInViewport) {
            handleImpression(Number(model.id))
          }
        }
      })
    }

    window.initItems = initItems
    initItems()

    return () => {
      delete window.handleImpression
      delete window.initItems
    }
  }, [handleImpression, models])

  return { isReady: !!pageConfig?.cart }
}

export default useCollectionHubTracking
